import * as am4charts from '@amcharts/amcharts4/charts';
import moment from 'moment-timezone';
import { IBatteryLevelData } from './batteryLevelChart';
import { ITzSmartBatteryLevel } from '../../../services/shared/battery/batteryCentricService';
import { TTypedTFunction } from '@lib/useTypedTranslation';
import { formatDate } from '../../core/device/compositions/performance/charts/deviceChargeLevel';
import { formatDateAxisLabelsInTimeZone } from '../../../components/chart/lib';

/**
 * Without `displayTimeZone` the selected day is the UTC day of `date` (legacy device-local data).
 * With it, `date` is a real UTC instant and the axis spans that instant's local day in the zone,
 * which can be 23, 24 or 25 real hours. The axis stays on real UTC; only the label text is
 * converted into the zone.
 */
export function drawXAxisLabels(xAxis: am4charts.DateAxis, date: number, displayTimeZone?: string) {
  const getDay = () => displayTimeZone ? moment.utc(date).tz(displayTimeZone) : moment.utc(date);
  xAxis.dataFields.data = "local";
  xAxis.min = getDay().startOf('day').valueOf();
  xAxis.max = getDay().endOf('day').valueOf();

  xAxis.dateFormatter.utc = true;
  xAxis.dateFormats.setKey("day", "dt");
  xAxis.markUnitChange = false;
  xAxis.renderer.labels.template.disabled = false;
  xAxis.renderer.labels.template.location = 0.001 as any;
  xAxis.baseInterval = { timeUnit: "second", count: 1 };
  xAxis.gridIntervals.setAll([
    { timeUnit: "hour", count: 1 },
    { timeUnit: "hour", count: 3 },
    { timeUnit: "hour", count: 6 }
  ]);
  formatDateAxisLabelsInTimeZone(xAxis, displayTimeZone);
}

export function drawYAxisLabelsLineChart(yAxis: am4charts.ValueAxis) {
  yAxis.min = 0;
  yAxis.max = 100;
  yAxis.strictMinMax = true;
  yAxis.renderer.minGridDistance = 30;
  yAxis.numberFormatter.numberFormat = "#'%'";
  yAxis.cursorTooltipEnabled = false;
  yAxis.contentAlign = 'right';
  yAxis.renderer.grid.template.disabled = false;
  yAxis.renderer.labels.template.disabled = true;
  yAxis.axisRanges.clear();
  createGrid(0, yAxis);
  createGrid(25, yAxis);
  createGrid(50, yAxis);
  createGrid(75, yAxis);
  createGrid(100, yAxis);
}

export function sortByDate(data: Array<any>): Array<any> {
  return data?.sort((a: any, b: any) => a.local - b.local);
}

/**
 * Ordinary callers provide their existing device-local data and position it by `local`. Zoned
 * callers provide records that require a real `utc` instant and position them by it, so repeated
 * fall-back wall-clock hours stay distinct. The chart's date field is named `local` either way.
 */
export function getData(data: IBatteryLevelData[], displayTimeZone?: undefined): Array<any>;
export function getData(data: ITzSmartBatteryLevel[], displayTimeZone: string): Array<any>;
export function getData(data: IBatteryLevelData[] | ITzSmartBatteryLevel[], displayTimeZone?: string): Array<any> {
  if (!data.length) {
    return [];
  }
  if (displayTimeZone) {
    return (data as ITzSmartBatteryLevel[])
      .map((item) => ({
        local: Number(item.utc),
        level: item.level,
        deviceSerialNumber: item.device?.serialNumber ?? '-'
      }))
      .sort((a, b) => a.local - b.local);
  }
  return sortByDate(data).map((item: any) => {
    return {
      local: parseInt(item.local),
      level: item.level,
      deviceSerialNumber: item.device?.serialNumber ?? '-'
    };
  });
}

export function createGrid(value: number, yAxis: am4charts.ValueAxis): void {
  let range = yAxis.axisRanges.create();
  range.label = new am4charts.AxisLabel();
  range.label.dy = -18;
  range.value = value;
  range.label.text = "{value}";
}

export const getTooltipAdapter = (t: TTypedTFunction, displayTimeZone?: string) => {
  return (_: any, target: am4charts.LineSeries) => {
    const tooltip = target.tooltipDataItem as any;
    const date = formatDate(tooltip.dateX, displayTimeZone);
    const colour = target?.dummyData?.colour;
    return `<div class="tooltip-container_device-charge-level"><span class="device-charge-level-24-hour-tooltip-title">${date}</span>
    </br><span class="line-chart-tooltip-container_device-charge-level"><div class="key" style="--colour: ${colour}"></div>${t('CHARGE_LEVEL')}: {valueY}</span>
    </br><span class="line-chart-tooltip-container-tooltip-text serialnumber">${t('DEVICE', { ns: 'translation' })} SN: ${tooltip.dataContext?.deviceSerialNumber}</span></div>`;
  };
};
